import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { useSession } from '../../lib/auth/session.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { Badge, ErrorBanner, CardHead } from '../../components/ui/Primitives.js';
import { ApiClientError } from '../../lib/api/client.js';
import { relativeTime } from '../../lib/format.js';

/**
 * The platform's own keys for the services it talks to (0095), entered here and sealed, the way a
 * Resend key and PayFast's merchant key already are. Nothing is ever read back: what is shown is
 * the last four characters, so somebody can tell which key is in without seeing it.
 *
 * Saving a key does not switch it on, and a key that has not been tested says so: finding out a key
 * is wrong when a customer's call fails to transcribe is the thing worth avoiding.
 */

interface Key {
  provider: string; role: 'primary' | 'admin';
  settings: Record<string, string>;
  secret_hint: string | null; enabled: boolean;
  last_tested_at: string | null; last_error: string | null;
  updated_at: string;
}
interface Spec { key: string; label: string; what: string; secret_label: string }

/**
 * A step-up refusal is not an error to show: it is the prompt's cue, and the prompt is already on
 * screen asking for the password. Showing both said "requires re-verifying your identity" in red
 * above a box that was waiting to do exactly that (owner, 20 September 2026).
 */
const realError = (e: unknown): unknown =>
  (e instanceof ApiClientError && e.code === 'auth.step_up_required' ? null : e);
interface Payload { data: Key[]; providers: Spec[] }

export function ServiceKeysCard() {
  const qc = useQueryClient();
  const can = useSession((s) => s.can);
  const writable = can('providerkey:write');
  const step = useStepUp();
  const [open, setOpen] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['platform-service-keys'],
    queryFn: () => api.get<Payload>('/v1/platform/service-keys'),
  });
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['platform-service-keys'] });
  };
  const test = useMutation({
    mutationFn: (v: { provider: string; role: string }) =>
      api.post<{ ok: boolean; error: string | null }>(
        `/v1/platform/service-keys/${v.provider}/${v.role}/actions/test`, {}),
    onSuccess: invalidate,
  });

  if (q.error) return <ErrorBanner error={q.error} />;

  const keys = q.data?.data ?? [];
  const specs = q.data?.providers ?? [];
  const find = (p: string, role = 'primary'): Key | undefined =>
    keys.find((k) => k.provider === p && k.role === role);

  return (
    <div className="arc-card" style={{ marginBottom: 14 }}>
      <CardHead title="Service keys" sub="the platform's own, used where an account has none" />
      <div className="arc-card-body">
        <StepUpPrompt step={step} what="Testing a service key" />
        {realError(test.error) ? <ErrorBanner error={test.error} /> : null}

        <div className="arc-stack" style={{ gap: 10 }}>
          {specs.map((spec) => {
            const k = find(spec.key);
            const isOpen = open === spec.key;
            return (
              <div key={spec.key} style={{
                border: '1px solid var(--arc-border)', borderRadius: 'var(--arc-r)', padding: '10px 12px',
              }}>
                <div className="arc-row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 13 }}>{spec.label}</strong>
                  {!k ? <Badge tone="muted">not set</Badge>
                    : k.enabled ? <Badge tone="ok">on</Badge>
                      : <Badge tone="muted">off</Badge>}
                  {k?.secret_hint && (
                    <span className="arc-mono arc-subtle" style={{ fontSize: 12 }}>
                      ····{k.secret_hint}
                    </span>
                  )}
                  {k && (k.last_error
                    ? <Badge tone="danger">last test failed</Badge>
                    : k.last_tested_at
                      ? <span className="arc-subtle" style={{ fontSize: 12 }}>
                          tested {relativeTime(k.last_tested_at)}
                        </span>
                      : <Badge tone="warn">never tested</Badge>)}
                  <div className="arc-spacer" />
                  {writable && k && (
                    <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                      disabled={test.isPending}
                      onClick={() => void step.run(() =>
                        test.mutateAsync({ provider: spec.key, role: 'primary' }))}>
                      Test
                    </button>
                  )}
                  {writable && (
                    <button type="button" className="arc-btn arc-btn-sm"
                      onClick={() => setOpen(isOpen ? null : spec.key)}>
                      {isOpen ? 'Close' : k ? 'Replace' : 'Add'}
                    </button>
                  )}
                </div>

                <p className="arc-hint" style={{ marginTop: 4 }}>{spec.what}</p>

                {k?.last_error && (
                  <p style={{ color: 'var(--arc-danger)', fontSize: 12, margin: '6px 0 0' }}>
                    {k.last_error}
                  </p>
                )}
                {k && spec.key.endsWith('_s3') && Object.keys(k.settings).length > 0 && (
                  <p className="arc-subtle arc-mono" style={{ fontSize: 11.5, margin: '6px 0 0' }}>
                    {Object.entries(k.settings).map(([n, v]) => `${n}: ${v}`).join(' · ')}
                  </p>
                )}
                {k && !spec.key.endsWith('_s3') && (
                  <ModelPicker provider={spec.key} current={k.settings.model ?? ''}
                    writable={writable} onSaved={invalidate} />
                )}

                {isOpen && (
                  <KeyForm spec={spec} existing={k ?? null}
                    onSaved={() => { setOpen(null); invalidate(); }} />
                )}
              </div>
            );
          })}
        </div>

        <p className="arc-hint" style={{ marginTop: 12 }}>
          A client that has its own keys uses those; these are what the platform falls back to.
        </p>
      </div>
    </div>
  );
}

function KeyForm({ spec, existing, onSaved }: {
  spec: Spec;
  existing: Key | null;
  onSaved: () => void;
}) {
  // Its own, so the password box appears under the form being saved rather than at the top of a
  // card listing four providers.
  const step = useStepUp();
  const [secret, setSecret] = useState('');
  const [enabled, setEnabled] = useState(existing?.enabled ?? true);
  const [bucket, setBucket] = useState(existing?.settings.bucket ?? '');
  const [region, setRegion] = useState(existing?.settings.region ?? 'af-south-1');
  const [keyId, setKeyId] = useState(existing?.settings.access_key_id ?? '');
  const [model, setModel] = useState(existing?.settings.model ?? '');
  const s3 = spec.key.endsWith('_s3');

  const save = useMutation({
    mutationFn: () => api.put(`/v1/platform/service-keys/${spec.key}/primary`, {
      ...(secret ? { secret } : {}),
      enabled,
      settings: s3
        ? { bucket: bucket.trim(), region: region.trim(), access_key_id: keyId.trim() }
        : (model.trim() ? { model: model.trim() } : {}),
    }),
    onSuccess: onSaved,
  });

  const missing = (!existing && !secret) || (s3 && (!bucket.trim() || !keyId.trim()));

  return (
    <div style={{ marginTop: 12, borderTop: '1px solid var(--arc-border)', paddingTop: 12 }}>
      {s3 && (
        <div className="arc-row" style={{ gap: 12, flexWrap: 'wrap' }}>
          <div className="arc-field" style={{ margin: 0, minWidth: 220 }}>
            <label className="arc-label" htmlFor={`${spec.key}-bucket`}>Bucket</label>
            <input id={`${spec.key}-bucket`} className="arc-input" value={bucket}
              placeholder="arcnet-recordings-af-south-1"
              onChange={(e) => setBucket(e.target.value)} />
          </div>
          <div className="arc-field" style={{ margin: 0, minWidth: 140 }}>
            <label className="arc-label" htmlFor={`${spec.key}-region`}>Region</label>
            <input id={`${spec.key}-region`} className="arc-input" value={region}
              onChange={(e) => setRegion(e.target.value)} />
          </div>
          <div className="arc-field" style={{ margin: 0, minWidth: 220 }}>
            <label className="arc-label" htmlFor={`${spec.key}-keyid`}>Access key id</label>
            <input id={`${spec.key}-keyid`} className="arc-input arc-mono" value={keyId}
              onChange={(e) => setKeyId(e.target.value)} />
          </div>
        </div>
      )}

      <div className="arc-row" style={{ gap: 12, flexWrap: 'wrap', marginTop: s3 ? 12 : 0 }}>
        <div className="arc-field" style={{ margin: 0, minWidth: 280, flex: 1 }}>
          <label className="arc-label" htmlFor={`${spec.key}-secret`}>{spec.secret_label}</label>
          <input id={`${spec.key}-secret`} className="arc-input arc-mono" type="password"
            autoComplete="new-password" value={secret}
            placeholder={existing ? 'leave blank to keep the current one' : ''}
            onChange={(e) => setSecret(e.target.value)} />
        </div>
        {!s3 && (
          <div className="arc-field" style={{ margin: 0, minWidth: 220 }}>
            <label className="arc-label" htmlFor={`${spec.key}-model`}>Default model</label>
            {/* autoComplete off: a browser filled this with an email address, which was then
                saved as the model (owner, 20 September 2026). */}
            <input id={`${spec.key}-model`} className="arc-input" value={model}
              autoComplete="off" spellCheck={false}
              placeholder="optional" onChange={(e) => setModel(e.target.value)} />
            <p className="arc-hint">Or leave it: once the key is saved, the list comes from the
              provider and is picked above.</p>
          </div>
        )}
      </div>

      <label className="arc-row" style={{ gap: 8, marginTop: 12, cursor: 'pointer' }}>
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        <span style={{ fontSize: 13 }}>Use this key</span>
      </label>

      {realError(save.error) ? <ErrorBanner error={save.error} /> : null}
      <StepUpPrompt step={step} what={`Saving the ${spec.label} key`} />

      <div className="arc-row" style={{ gap: 8, marginTop: 12 }}>
        <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
          disabled={missing || save.isPending}
          onClick={() => void step.run(() => save.mutateAsync())}>
          {save.isPending ? 'Saving…' : 'Save key'}
        </button>
        <button type="button" className="arc-btn arc-btn-sm" onClick={onSaved}>Cancel</button>
        <span className="arc-subtle" style={{ fontSize: 12 }}>
          Sealed on the server. It is never shown again.
        </span>
      </div>
    </div>
  );
}

/**
 * Which model this key uses, from a list the provider itself gives.
 *
 * Not typed in and not hard-coded here: model names change faster than we deploy, and a stale list
 * is a support call (owner, 20 September 2026). The list is fetched with the stored key, refreshed
 * on demand, and changing it never means entering the key again.
 */
function ModelPicker({ provider, current, writable, onSaved }: {
  provider: string; current: string; writable: boolean; onSaved: () => void;
}) {
  const step = useStepUp();
  const [open, setOpen] = useState(false);

  const models = useQuery({
    queryKey: ['platform-service-keys', provider, 'models'] as const,
    queryFn: () => api.get<{ models: string[]; error: string | null }>(
      `/v1/platform/service-keys/${provider}/primary/models`),
    enabled: open,
    // Fetched when it is opened, and again only when asked: a provider's model list is not
    // something to poll.
    staleTime: Infinity,
  });

  const save = useMutation({
    mutationFn: (model: string) => api.put(`/v1/platform/service-keys/${provider}/primary`, {
      settings: model ? { model } : {},
    }),
    onSuccess: onSaved,
  });

  const list = models.data?.models ?? [];
  const known = current && !list.includes(current) ? [current, ...list] : list;

  return (
    <div style={{ marginTop: 6 }}>
      <div className="arc-row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className="arc-subtle" style={{ fontSize: 12 }}>
          Model: <span className="arc-mono">{current || 'the provider\u2019s default'}</span>
        </span>
        {writable && !open && (
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
            onClick={() => setOpen(true)}>Change</button>
        )}
      </div>

      {open && (
        <div className="arc-row" style={{ gap: 8, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="arc-select" style={{ maxWidth: 320 }} defaultValue={current}
            disabled={models.isFetching || save.isPending}
            onChange={(e) => void step.run(() => save.mutateAsync(e.target.value))}>
            <option value="">The provider's default</option>
            {known.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
            disabled={models.isFetching}
            onClick={() => void models.refetch()}>
            {models.isFetching ? 'Reading\u2026' : 'Refresh list'}
          </button>
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
            onClick={() => setOpen(false)}>Done</button>
          {models.data?.error && (
            <span className="arc-subtle" style={{ fontSize: 12 }}>
              Could not read the list: {models.data.error}
            </span>
          )}
          {!models.isFetching && !models.data?.error && list.length > 0 && (
            <span className="arc-subtle" style={{ fontSize: 12 }}>{list.length} available</span>
          )}
        </div>
      )}
      <StepUpPrompt step={step} what="Changing the model" />
      {realError(save.error) ? <ErrorBanner error={save.error} /> : null}
    </div>
  );
}
